import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  getMemoryCache,
  setMemoryCache,
  invalidateMemoryCache,
  getOrFetchMemoryCache,
} from '../utils/memoryCache';

const USER_CACHE_TTL_MS = 5_000;
const ADMIN_PROFILE_TTL_MS = 30_000;
const ADMIN_PHOTO_TTL_MS = 5 * 60_000;
const SUPPORT_MESSAGES_TTL_MS = 5_000;

// -----------------------------
// Auth helpers
// -----------------------------
export async function getSupabaseUser() {
  const cachedUser = getMemoryCache('auth:user', USER_CACHE_TTL_MS);
  if (cachedUser !== undefined) return cachedUser;

  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) return null;
    return setMemoryCache('auth:user', user || null);
  } catch {
    return null;
  }
}

export function useSupabaseUser() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const u = await getSupabaseUser();
      if (mounted) {
        setUser(u);
        setLoading(false);
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      const nextUser = session?.user ?? null;
      setMemoryCache('auth:user', nextUser);
      setUser(nextUser);
    });

    return () => {
      mounted = false;
      try {
        subscription?.subscription?.unsubscribe?.();
        subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  return { user, loading };
}

// -----------------------------
// Admin profile helpers
// -----------------------------
// Cache module-level : évite de refetcher admin_Profile à chaque page.
// TTL court (30s) + invalidation manuelle via invalidateAdminProfileCache().
const __adminProfileCache = new Map(); // userId -> { data, fetchedAt }

export function invalidateAdminProfileCache(userId) {
  if (userId) {
    __adminProfileCache.delete(userId);
    invalidateMemoryCache(`admin-profile:${userId}`);
  } else {
    __adminProfileCache.clear();
    invalidateMemoryCache();
  }
}

export async function fetchAdminProfile(userId, { force = false } = {}) {
  if (!userId) return null;
  const cacheKey = `admin-profile:${userId}`;
  if (!force) {
    const sharedCached = getMemoryCache(cacheKey, ADMIN_PROFILE_TTL_MS);
    if (sharedCached !== undefined) return sharedCached;
  }
  const cached = __adminProfileCache.get(userId);
  if (!force && cached && Date.now() - cached.fetchedAt < ADMIN_PROFILE_TTL_MS) {
    return cached.data;
  }
  try {
    const { data, error } = await supabase
      .from('admin_Profile')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return cached?.data || null;
    const value = data || null;
    __adminProfileCache.set(userId, { data: value, fetchedAt: Date.now() });
    setMemoryCache(cacheKey, value);
    return value;
  } catch {
    return cached?.data || null;
  }
}

export async function ensureAdminProfile(user) {
  try {
    if (!user) return null;
    const { data: profile, error: profileError } = await supabase
      .from('admin_Profile')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profile && !profileError) return profile;

    const meta = user.user_metadata || {};
    const newProfile = {
      user_id: user.id,
      first_name: meta.first_name || meta.full_name || '',
      last_name: meta.last_name || '',
      First_name: meta.First_name || meta.first_name || meta.full_name || '',
      Last_name: meta.Last_name || meta.last_name || '',
      phone: meta.phone || null,
      governorate: meta.governorate || null,
      role: meta.role || null,
      status: true,
      fees: meta.fees || [],
    };

    const { data: insertData, error: insertError } = await supabase
      .from('admin_Profile')
      .insert(newProfile)
      .select()
      .single();

    if (insertError) {
      // ignore, try to refetch
    }

    const { data: refetchProfile } = await supabase
      .from('admin_Profile')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    return refetchProfile || insertData || null;
  } catch {
    return null;
  }
}

export async function resolveAdminPhotoUrlByUserId(userId) {
  try {
    if (!userId) return '';
    return await getOrFetchMemoryCache(`admin-photo:${userId}`, async () => {
      const { data: adminProfile } = await supabase
        .from('admin_Profile')
        .select('photo')
        .eq('user_id', userId)
        .maybeSingle();

      const cand = adminProfile?.photo;
      if (!cand) return '';
      if (typeof cand === 'string' && (cand.startsWith('http://') || cand.startsWith('https://'))) {
        return cand;
      }
      const tryPaths = [cand, `photos/${cand}`, `admin/${cand}`].filter(Boolean);
      for (const p of tryPaths) {
        try {
          const pub = supabase.storage.from('admin').getPublicUrl(p);
          const publicUrl = pub?.data?.publicUrl || pub?.publicUrl || '';
          if (publicUrl) return publicUrl;
        } catch {}
      }
      return '';
    }, ADMIN_PHOTO_TTL_MS);
  } catch {
    return '';
  }
}

// -----------------------------
// Notifications helpers
// -----------------------------

/**
 * Récupère les notifications adressées à l'utilisateur connecté.
 * Filtre direct sur notifications.user_id == connectedUserId.
 *
 * @param {string} connectedUserId - user_id de l'admin connecté (destinataire)
 */
export async function fetchSupportMessages(connectedUserId, { force = false } = {}) {
  if (!connectedUserId) return { messages: [], unreadCount: 0 };
  const notificationsCacheKey = `support-messages:${connectedUserId}`;
  if (!force) {
    const cached = getMemoryCache(notificationsCacheKey, SUPPORT_MESSAGES_TTL_MS);
    if (cached !== undefined) return cached;
  }
  try {
    const { data: notifData, error: notifError } = await supabase
      .from('notifications')
      .select('id, user_id, sender_id, type, description, created_at, read, updated_at, firstname, lastname, imageavatar')
      .eq('user_id', connectedUserId)
      .order('created_at', { ascending: false });

    if (notifError) {
      console.error('[notifications] error:', notifError);
      return { messages: [], unreadCount: 0 };
    }

    const rawList = notifData || [];
    if (rawList.length === 0) {
      return setMemoryCache(notificationsCacheKey, { messages: [], unreadCount: 0 });
    }

    // Les colonnes firstname / lastname / imageavatar sont stockées
    // directement sur la table notifications (le sender_id peut être vide).
    const messages = rawList.map(notif => ({
      id: notif.id,
      user_id: notif.user_id,
      sender_id: notif.sender_id || null,
      type: notif.type,
      description: notif.description,
      firstname: notif.firstname || '',
      lastname: notif.lastname || '',
      imageavatar: notif.imageavatar || null,
      matricule: null,
      message: notif.description || '',
      status: 'open',
      read: notif.read,
      created_at: notif.created_at,
      updated_at: notif.updated_at,
      image_urls: []
    }));

    // Enrichissement complémentaire selon le type :
    //  - type === 'discount' : l'expéditeur est un admin → admin_Profile
    //    (First_name / Last_name / photo)
    //  - autres types : expéditeur est un utilisateur standard → profiles
    //    (firstname / lastname / imageavatar / Matricule)
    try {
      const discountMsgs = messages.filter(m => m.type === 'discount' && m.sender_id);
      const otherMsgs = messages.filter(m => m.type !== 'discount' && m.sender_id);

      // Enrichissement depuis admin_Profile (type=discount)
      const adminSenderIds = [...new Set(discountMsgs.map(m => m.sender_id))];
      if (adminSenderIds.length > 0) {
        const { data: adminProfiles } = await supabase
          .from('admin_Profile')
          .select('user_id, First_name, Last_name, photo')
          .in('user_id', adminSenderIds);
        const adminMap = {};
        (adminProfiles || []).forEach(p => { adminMap[String(p.user_id)] = p; });
        discountMsgs.forEach(m => {
          const p = adminMap[String(m.sender_id)];
          if (!p) return;
          m.firstname = p.First_name || m.firstname || '';
          m.lastname = p.Last_name || m.lastname || '';
          m.imageavatar = p.photo || m.imageavatar || null;
          m.matricule = null;
        });
      }

      // Enrichissement depuis profiles (autres types)
      const profileSenderIds = [...new Set(otherMsgs.map(m => m.sender_id))];
      if (profileSenderIds.length > 0) {
        const { data: senderProfiles } = await supabase
          .from('profiles')
          .select('id, firstname, lastname, imageavatar, Matricule')
          .in('id', profileSenderIds);
        const senderMap = {};
        (senderProfiles || []).forEach(p => { senderMap[String(p.id)] = p; });
        otherMsgs.forEach(m => {
          const p = senderMap[String(m.sender_id)];
          if (!p) return;
          m.firstname = p.firstname || m.firstname || '';
          m.lastname = p.lastname || m.lastname || '';
          m.imageavatar = p.imageavatar || m.imageavatar || null;
          m.matricule = p.Matricule || null;
        });
      }
    } catch (_) {
      // ignore enrich error — la notification garde firstname/lastname/imageavatar de la table
    }

    const unreadCount = messages.filter(
      m => m.read === false || m.read === 'false' || m.read === null
    ).length;

    return setMemoryCache(notificationsCacheKey, { messages, unreadCount });
  } catch (err) {
    console.error('[notifications] catch error:', err);
    return { messages: [], unreadCount: 0 };
  }
}

/**
 * Marque une notification comme lue
 */
export async function markSupportMessageAsRead(notificationId) {
  if (!notificationId) return { error: new Error('Missing notificationId') };
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);
    invalidateMemoryCache();
    return { error: error || null };
  } catch (e) {
    return { error: e };
  }
}

/**
 * Supprime une notification
 */
export async function deleteSupportMessage(notificationId) {
  if (!notificationId) return { error: new Error('Missing notificationId') };
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);
    invalidateMemoryCache();
    return { error: error || null };
  } catch (e) {
    return { error: e };
  }
}

/**
 * Hook Realtime — affiche les notifs adressées à l'utilisateur connecté
 * (filtre direct sur notifications.user_id == userId courant).
 */
export function useSupportMessages() {
  const [state, setState] = useState({
    messages: [],
    unreadCount: 0,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;
    let channel = null;
    let currentUserId = null;

    const setupForUser = async (userId) => {
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }
      currentUserId = userId;

      if (!userId) {
        if (mounted) setState({ messages: [], unreadCount: 0, loading: false });
        return;
      }

      const res = await fetchSupportMessages(userId);
      if (mounted) setState({ ...res, loading: false });

      // Nom de canal unique par instance du hook pour éviter la dédup côté
      // Supabase quand plusieurs composants utilisent useSupportMessages.
      const channelName = `notifications_realtime_${userId}_${Math.random().toString(36).slice(2, 10)}`;
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
          async () => {
            const res = await fetchSupportMessages(userId, { force: true });
            if (mounted) setState(prev => ({ ...prev, ...res, loading: false }));
          }
        )
        .subscribe();
    };

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      await setupForUser(user?.id || null);
    })();

    // Re-subscribe quand l'auth change (login/logout/refresh)
    const { data: authSub } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id || null;
      if (newUserId !== currentUserId) {
        setupForUser(newUserId);
      }
    });

    return () => {
      mounted = false;
      if (channel) supabase.removeChannel(channel);
      authSub?.subscription?.unsubscribe?.();
    };
  }, []);

  return state;
}

// -----------------------------
// Partner upgrade requests — pending count (realtime + event bus local)
// -----------------------------

// Bus local : à appeler après chaque mutation locale qui touche
// partner_upgrade_requests (approve/reject/cancel/promote/etc.) pour
// rafraîchir immédiatement le badge sans attendre le postgres_changes.
export function notifyPartnerRequestsChanged() {
  try {
    window.dispatchEvent(new CustomEvent('partner_upgrade_requests:changed'));
  } catch {
    // SSR ou window indisponible : silencieux
  }
}

export function usePendingPartnerRequestsCount() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let channel = null;

    const refresh = async () => {
      try {
        const { count: c, error } = await supabase
          .from('partner_upgrade_requests')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending');
        if (!mounted) return;
        if (error) {
          setCount(0);
        } else {
          setCount(c || 0);
        }
      } catch {
        if (mounted) setCount(0);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    refresh();

    // 1) Realtime postgres_changes (changements externes — app mobile, autre admin)
    const channelName = `partner_upgrade_requests_pending_${Math.random().toString(36).slice(2, 10)}`;
    channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'partner_upgrade_requests' },
        () => {
          refresh();
        }
      )
      .subscribe();

    // 2) Bus local — chaque mutation faite depuis l'UI émet cet event
    //    et le badge se met à jour instantanément, même si le realtime
    //    Supabase n'est pas configuré sur la publication.
    const onLocalChange = () => { refresh(); };
    window.addEventListener('partner_upgrade_requests:changed', onLocalChange);

    return () => {
      mounted = false;
      if (channel) supabase.removeChannel(channel);
      window.removeEventListener('partner_upgrade_requests:changed', onLocalChange);
    };
  }, []);

  return { count, loading };
}

// -----------------------------
// Activate end user account
// -----------------------------
export async function activateEndUser(targetUserId) {
  if (!targetUserId) return { ok: false, activationTimestamp: null, deactivationTimestamp: null, error: new Error('Missing targetUserId') };
  try {
    const activationTimestamp = new Date().toISOString();
    const deactivationDate = new Date();
    deactivationDate.setDate(deactivationDate.getDate() + 30);
    const deactivationTimestamp = deactivationDate.toISOString();

    const { error } = await supabase
      .from('profiles')
      .update({
        is_activated: true,
        activated_at: activationTimestamp,
        deactivated_at: deactivationTimestamp,
      })
      .eq('id', targetUserId);

    if (error) return { ok: false, activationTimestamp: null, deactivationTimestamp: null, error };
    return { ok: true, activationTimestamp, deactivationTimestamp, error: null };
  } catch (e) {
    return { ok: false, activationTimestamp: null, deactivationTimestamp: null, error: e };
  }
}

// -----------------------------
// Batch fetch helpers
// -----------------------------
export async function fetchProfilesByIds(ids = []) {
  try {
    const clean = ids.filter(id => !!id && typeof id === 'string' && id !== 'undefined');
    if (clean.length === 0) return [];
    const { data, error } = await supabase
      .from('profiles')
      .select('id, firstname, lastname, imageavatar, Matricule, profession')
      .in('id', clean);
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

export async function fetchAdminProfilesByIds(ids = []) {
  try {
    const clean = ids.filter(id => !!id && typeof id === 'string' && id !== 'undefined');
    if (clean.length === 0) return [];
    const { data, error } = await supabase
      .from('admin_Profile')
      .select('user_id, First_name, Last_name, photo')
      .in('user_id', clean);
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// -----------------------------
// Convenience hooks
// -----------------------------
export function useAdminPhoto(userId) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let mounted = true;
    (async () => {
      const u = await resolveAdminPhotoUrlByUserId(userId);
      if (mounted) setUrl(u || '');
    })();
    return () => { mounted = false; };
  }, [userId]);
  return url;
}

// -----------------------------
// Auth routing + UI helpers
// -----------------------------
export function useRequireAuth(redirectTo = '/sign-in') {
  const history = useHistory();
  const { user, loading } = useSupabaseUser();
  useEffect(() => {
    if (!loading && !user) {
      history.push(redirectTo);
    }
  }, [loading, user, redirectTo, history]);
  return { user, userId: user?.id || null, loading };
}

export function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  useEffect(() => {
    function onResize() {
      setWidth(window.innerWidth);
    }
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

// -----------------------------
// Generic CRUD for job tables
// -----------------------------
function normalizeTableName(table) {
  if (!table) return '';
  return String(table).trim();
}

export async function listJobItems(table) {
  const t = normalizeTableName(table);
  try {
    // For artisant table, don't select price column (may not exist for that table)
    const columns = t === 'artisant' 
      ? 'id, jobTitle, iconTitle'
      : 'id, jobTitle, iconTitle';
    const { data, error } = await supabase
      .from(t)
      .select(columns);
    if (error) {
      console.error(`Error fetching from ${t}:`, error);
      throw error;
    }
    return data || [];
  } catch (e) {
    console.error(`listJobItems error for table ${t}:`, e);
    return [];
  }
}

export async function createJobItem(table, { jobTitle, iconTitle, price } = {}) {
  const t = normalizeTableName(table);
  try {
    const payload = { jobTitle };
    // artisant uses icons like company
    if (typeof iconTitle !== 'undefined' && iconTitle !== null && iconTitle !== '') {
      payload.iconTitle = iconTitle;
    }
    if (typeof price !== 'undefined' && price !== null) {
      payload.price = price;
    }
    const { data, error } = await supabase.from(t).insert([payload]).select();
    if (error) {
      console.error(`createJobItem error for table ${t}:`, error);
      throw error;
    }
    return { data: (data && data[0]) || null, error: null };
  } catch (e) {
    console.error(`createJobItem catch error for table ${t}:`, e);
    return { data: null, error: e };
  }
}

export async function updateJobItem(table, id, { jobTitle, iconTitle, price } = {}) {
  const t = normalizeTableName(table);
  try {
    const payload = {};
    if (typeof jobTitle !== 'undefined' && jobTitle !== null) payload.jobTitle = jobTitle;
    // artisant uses icons like company
    if (typeof iconTitle !== 'undefined' && iconTitle !== null && iconTitle !== '') payload.iconTitle = iconTitle;
    if (typeof price !== 'undefined' && price !== null) payload.price = price;
    const { data, error } = await supabase.from(t).update(payload).eq('id', id).select();
    if (error) {
      console.error(`updateJobItem error for table ${t}:`, error);
      throw error;
    }
    return { data: (data && data[0]) || null, error: null };
  } catch (e) {
    console.error(`updateJobItem catch error for table ${t}:`, e);
    return { data: null, error: e };
  }
}

export async function deleteJobItem(table, id) {
  const t = normalizeTableName(table);
  try {
    const { error } = await supabase.from(t).delete().eq('id', id);
    return { error: error || null };
  } catch (e) {
    return { error: e };
  }
}

export async function applyGlobalPriceToTable(table, price) {
  const t = normalizeTableName(table);
  try {
    const numeric = parseFloat(price);
    if (Number.isNaN(numeric)) return { error: new Error('Invalid price') };
    const { error } = await supabase.from(t).update({ price: numeric }).gt('id', 0);
    return { error: error || null };
  } catch (e) {
    return { error: e };
  }
}